/**
 * The sky and shadow paints (`descent-dojo --sky --self` / `--shadow --self`)
 * and the ring debug controls (`descent-dojo --rings`).
 *
 * Both paints are lies the instruments tell the frame so their readers can be
 * validated against a known answer before anything real is drawn there
 * (docs/VERIFICATION.md). Inert until set.
 */

import * as THREE from 'three';

import type { AtlasLive } from '../atlas';
import type { AtmosphereViewHandle } from './atmosphereView.ts';
import type { BodyReadersHandle } from './bodyReaders.ts';

export interface DojoPaintsDeps {
  readonly atlasRoot: THREE.Object3D;
  readonly renderer: THREE.WebGLRenderer;
  readonly bodyReaders: BodyReadersHandle;
  readonly atmosphereView: AtmosphereViewHandle;
  own(fn: () => void): void;
  readonly live: AtlasLive;
}

export interface DojoPaintsHandle {
  readonly skyDebug: {
    flatSky: number | null; injectFrom: string | null; legacySun: boolean; progradeSpin: boolean; seasonSun: boolean;
    slidingSun: boolean; eyeOnDatum: boolean;
  };
  /** `csmOverride` null follows the band (`updateShadowCascades`); false is
   *  the negative control, forcing every cascade's contribution to full
   *  visibility without tearing down the lights. */
  readonly shadowDebug: { fakeContact: boolean; csmOverride: boolean | null };
  /** `descent-dojo --rings`'s controls; null follows the profile and the
   *  registry's own `rings.particle_albedo` / `rings.brightness_gain`.
   *  `suppressNote` is `--inject gain-undeclared`: the stretch applied and the
   *  card and overlay told to say nothing, which `declared` has to catch. */
  readonly ringDebug: {
    flatTau: number | null; albedo: number | null; pointSample: boolean;
    gain: number | null; suppressNote: boolean; suppress: boolean;
    componentScale: { index: number; factor: number } | null;
  };
  /** The pane's height in CSS pixels: what a label's box is reported in. */
  labelViewportH(): number;
  /**
   * A painted frame, for `descent-dojo --sky --self`.
   *
   * TWO bands, not one flat colour: the arm reads the frame's UPPER third out
   * of a readback whose rows run bottom-up, and a single colour passes with
   * that mapping inverted. The top band is the colour asked for and the bottom
   * its complement, so a flipped read lands on a value it cannot mistake and
   * the band edge gives the horizon finder a step at a known row. Drawn over
   * everything with no depth test, and written straight to the framebuffer -
   * a raw ShaderMaterial gets no tone map and no colour transform - so the
   * byte painted is the byte the readback returns.
   */
  setFlatSky(hex: number | null): void;
  /**
   * Another body's scattering sky, drawn here: `descent-dojo --inject-sky <id>`.
   *
   * The `vacuum` criterion's negative control. An airless body mounts no
   * atmosphere at all, so there is nothing to corrupt in place - this mounts the
   * NAMED body's tables and points `atmoBodyId` at the ground under the camera,
   * which is the wrongly-routed sky the criterion has to fail on. Refused on a
   * body that mounted its own, since that sky is not the one under test, and on
   * a source row that declares no atmosphere to build from.
   */
  setInjectSky(id: string | null): void;
  /**
   * A painted contact shadow under the rover, for `descent-dojo --shadow --self`.
   *
   * A DECAL, deliberately: it darkens the annulus the arm reads and it does not
   * move when the sun does, which is the fault the arm's azimuth half exists to
   * catch. Flat, and lifted above the highest ground it covers rather than
   * draped, because the arm reads a mean over pixels and not a silhouette.
   */
  setFakeContact(on: boolean): void;
}

export function createDojoPaints(deps: DojoPaintsDeps): DojoPaintsHandle {
  const { atlasRoot, renderer, own } = deps;
  const { ladderBody, rowById } = deps.bodyReaders;
  const { mountAtmo, unmountAtmo } = deps.atmosphereView;

  const skyDebug: {
    flatSky: number | null; injectFrom: string | null; legacySun: boolean; progradeSpin: boolean; seasonSun: boolean;
    slidingSun: boolean; eyeOnDatum: boolean;
  } = { flatSky: null, injectFrom: null, legacySun: false, progradeSpin: false, seasonSun: false, slidingSun: false,
    eyeOnDatum: false };
  let flatSkyMesh: THREE.Mesh | null = null;
  /** `csmOverride` null follows the band (`updateShadowCascades`); false is
   *  the negative control, forcing every cascade's contribution to full
   *  visibility without tearing down the lights. */
  const shadowDebug: { fakeContact: boolean; csmOverride: boolean | null } =
    { fakeContact: false, csmOverride: null };
  const _labelSize = new THREE.Vector2();
  function labelViewportH(): number {
    renderer.getSize(_labelSize);
    return _labelSize.y;
  }
  /** `descent-dojo --rings`'s controls; null follows the profile and the
   *  registry's own `rings.particle_albedo` / `rings.brightness_gain`.
   *  `suppressNote` is `--inject gain-undeclared`: the stretch applied and the
   *  card and overlay told to say nothing, which `declared` has to catch. */
  const ringDebug: {
    flatTau: number | null; albedo: number | null; pointSample: boolean;
    gain: number | null; suppressNote: boolean; suppress: boolean;
    componentScale: { index: number; factor: number } | null;
  } = { flatTau: null, albedo: null, pointSample: false, gain: null, suppressNote: false,
    suppress: false, componentScale: null };
  let fakeContactMesh: THREE.Mesh | null = null;
  /** Wide enough to cover the arm's contact annulus, well inside its 3 m ring. */
  const FAKE_CONTACT_R_M = 3.0;

  function dropFlatSky(): void {
    if (!flatSkyMesh) return;
    atlasRoot.remove(flatSkyMesh);
    flatSkyMesh.geometry.dispose();
    (flatSkyMesh.material as THREE.Material).dispose();
    flatSkyMesh = null;
  }

  function setFlatSky(hex: number | null): void {
    skyDebug.flatSky = hex;
    deps.live.needsRender = true;
    if (hex === null) { dropFlatSky(); return; }
    const top = new THREE.Vector3(
      ((hex >> 16) & 255) / 255, ((hex >> 8) & 255) / 255, (hex & 255) / 255);
    const bottom = new THREE.Vector3(1 - top.x, 1 - top.y, 1 - top.z);
    if (flatSkyMesh) {
      const u = (flatSkyMesh.material as THREE.ShaderMaterial).uniforms;
      (u['uTop']!.value as THREE.Vector3).copy(top);
      (u['uBottom']!.value as THREE.Vector3).copy(bottom);
      return;
    }
    const mat = new THREE.ShaderMaterial({
      uniforms: { uTop: { value: top }, uBottom: { value: bottom } },
      vertexShader: /* glsl */ `
        varying vec2 vUv;
        void main() { vUv = uv; gl_Position = vec4(position.xy * 2.0, 0.0, 1.0); }`,
      fragmentShader: /* glsl */ `
        varying vec2 vUv;
        uniform vec3 uTop;
        uniform vec3 uBottom;
        void main() { gl_FragColor = vec4(vUv.y > 0.5 ? uTop : uBottom, 1.0); }`,
      depthTest: false,
      depthWrite: false,
      // In the TRANSPARENT pass, which is the only way to be last regardless
      // of render order: atmoSky's own dome and the terrain both draw in the
      // OPAQUE pass, which three.js always finishes first.
      transparent: true,
    });
    flatSkyMesh = new THREE.Mesh(new THREE.PlaneGeometry(1, 1), mat);
    flatSkyMesh.frustumCulled = false;
    flatSkyMesh.renderOrder = 1e6;
    atlasRoot.add(flatSkyMesh);
  }

  function setInjectSky(id: string | null): void {
    if (id === null) {
      if (skyDebug.injectFrom === null) return;
      skyDebug.injectFrom = null;
      unmountAtmo();
      deps.live.needsRender = true;
      return;
    }
    if (skyDebug.injectFrom !== null) return;
    // The focused body's own air is not the sky under test. A primary's drawn
    // behind it is not either, and that one comes down to make room: a moon of
    // an atmospheric body mounts tables of its own accord now.
    if (deps.atmosphereView.atmo && deps.atmosphereView.atmoBodyId === ladderBody()) return;
    const row = rowById(id);
    if (!row || row.atmosphere.kind !== 'data') return;
    skyDebug.injectFrom = id;
    unmountAtmo();
    mountAtmo(row, ladderBody());
    deps.live.needsRender = true;
  }

  function dropFakeContact(): void {
    if (!fakeContactMesh) return;
    fakeContactMesh.removeFromParent();
    fakeContactMesh.geometry.dispose();
    (fakeContactMesh.material as THREE.Material).dispose();
    fakeContactMesh = null;
  }

  function setFakeContact(on: boolean): void {
    shadowDebug.fakeContact = on;
    deps.live.needsRender = true;
    if (!on || !deps.live.plot?.rover) { dropFakeContact(); return; }
    dropFakeContact();
    const r = deps.live.plot.rover;
    const f = deps.live.plot.field;
    const minM = f.meta.elevation.min_m;
    let hi = f.heightAt(r.state.x, r.state.z);
    for (let i = 0; i < 8; i++) {
      const a = (i / 8) * Math.PI * 2;
      hi = Math.max(hi, f.heightAt(
        r.state.x + FAKE_CONTACT_R_M * Math.sin(a), r.state.z - FAKE_CONTACT_R_M * Math.cos(a)));
    }
    const geo = new THREE.CircleGeometry(FAKE_CONTACT_R_M, 48);
    geo.rotateX(-Math.PI / 2);
    fakeContactMesh = new THREE.Mesh(geo, new THREE.MeshBasicMaterial({ color: 0x000000 }));
    fakeContactMesh.position.set(
      r.state.x, (hi - minM) * deps.live.plot.exaggeration + 0.1, r.state.z);
    deps.live.plot.group.add(fakeContactMesh);
  }

  own(() => { dropFlatSky(); dropFakeContact(); });

  return {
    skyDebug,
    shadowDebug,
    ringDebug,
    labelViewportH,
    setFlatSky,
    setInjectSky,
    setFakeContact,
  };
}
